import React from "react";
import { Card, Alert } from 'react-bootstrap';
import { connect } from "react-redux";
import { ValidatableForm } from "../../components/ValidatableForm";
import { QUOTE_STEPS, COMPONENTS, QUOTE_QUESTIONS } from "../../AppConstants";
import { validationUtils } from "../../validation/Validation";
import { IQuoteStepState, IQuoteStepComponentProps } from "../../components/interfaces";
import ButtonContinue from "../../components/ButtonContinue";
import { Button } from 'react-bootstrap';
import { IQuoteApiClient, ILOBApiClient, IPaths, ICoverage, IViolationList } from "../../classes/Domain";
import QuoteActions from "../../state/appActions";
import { IVehicle, ICoverageConfig, IDriver, IContactInfo, IDiscountOption } from "../../classes/Domain";
import DriverPart2 from "../../components/DriverPart2";
import QuoteStepBase from "../../components/QuoteStepBase";
import { QuickQuoteStep } from "../QuickQuote/QuickQuoteStepLayout";
import { IAppState } from "../../state/interfaces";
import SwiperCore, { Pagination } from 'swiper';
import {
    getDefaultDlLicenseState,
    getDefaultLiabilityCoverages,
    getStateQuoteConfig,
    hasQuickQuoteDiscounts,
} from "../../config/StateQuoteConfig";
import { ageFromBirthDate } from "../../config/driverLicenseExperience";

interface IAppStateUpdate {
    quoteUid: string,
    drivers: Array<IDriver>,
    vehicles: Array<IVehicle>,
    coverageConfig: ICoverageConfig,
    contactInfo: IContactInfo,
    discounts: IDiscountOption,
    isNonOwners: boolean,
    quickStep: string,
    stateCode: string
}

interface IState extends IQuoteStepState {
    drivers: Array<IDriver>,
    vehicles: Array<IVehicle | null>,
    coverageConfig: ICoverageConfig,
    activeVehicleIndex: number,
    activeDriverIndex: number,
    canAddNewVehicle: boolean,
    canAddNewDriver: boolean,
    continueClick: boolean,
    contactInfo: IContactInfo,
    discounts: IDiscountOption,
    isNonOwners: boolean,
    loadingViolationsList: boolean,
    /** Hydrate from redux once — avoid re-merge on every connect tick (clears loading / wipes edits). */
    propsMerged: boolean
}

export interface IProps extends IQuoteStepComponentProps {
    stateCode: string,
    quoteApiClient: IQuoteApiClient,
    lobApiClient: ILOBApiClient,
    drivers: Array<IDriver>,
    vehicles: Array<IVehicle>,
    rateError: string | null,
    PATHS: IPaths;
    violationList: IViolationList | null;
    updateViolationList: (update: IViolationList) => Promise<any>;
}

function requireAgeGenderFor(stateCode: string): boolean {
    return !!getStateQuoteConfig(stateCode)?.requireDriverAgeGender;
}

function isDriverReady(driver: IDriver | null | undefined, requireAgeGender: boolean, driverIndex: number = 0): boolean {
    if (!driver) return false;
    if (!driver.firstName || !driver.lastName) return false;
    if (!driver.birthDate || driver.birthDate.length < 10) return false;
    if (!driver.licenseState) return false;
    if (!driver.maritalStatus) return false;
    if (!driver.currentLicenseStatus) return false;
    if (!driver.accidentsViolations) return false;
    if (requireAgeGender && !driver.gender) return false;
    const age = ageFromBirthDate(driver.birthDate);
    const minAge = driverIndex === 0 ? 18 : 16;
    if (age < minAge || age > 99) return false;
    return true;
}

function createEmptyDriver(stateCode: string): IDriver {
    return {
        currentLicenseStatus: QUOTE_QUESTIONS.YOUR_LICENSE.CURRENT_LICENSE_STATUS.ACTIVE,
        licenseState: getDefaultDlLicenseState(stateCode),
    } as IDriver;
}

class QuickDriver extends QuoteStepBase<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.next = this.next.bind(this);
        this.onDriverAdd = this.onDriverAdd.bind(this);
        this.onDriverDelete = this.onDriverDelete.bind(this);
        this.onDriverChange = this.onDriverChange.bind(this);
        this.onPhoneChange = this.onPhoneChange.bind(this);

        SwiperCore.use([Pagination]);

        this.state = {
            drivers: [createEmptyDriver(this.props.stateCode)],
            vehicles: [{} as IVehicle],
            isNonOwners: false,
            coverageConfig: {
                liabilityCoverages: getDefaultLiabilityCoverages(this.props.stateCode),
                addonCoverages: [],
                vehicleCoverages: [{
                    vehicleId: 0,
                    coverages: [{} as ICoverage]
                }]
            } as ICoverageConfig,
            activeVehicleIndex: 0,
            activeDriverIndex: 0,
            canAddNewVehicle: false,
            canAddNewDriver: false,
            continueClick: false,
            contactInfo: { phone: '', email: '' } as IContactInfo,
            discounts: {
                installments: true,
                paperless: true
            } as IDiscountOption,
            updatingStore: false,
            loadingViolationsList: false,
            propsMerged: false,
            errors: null
        };
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.reportCurrentStep();

        if (!this.props.violationList) {
            this.setState({ loadingViolationsList: true });
            this.props.lobApiClient.getViolationList(this.props.stateCode)
                .then((response) => this.props.updateViolationList(response.data))
                .catch(() => { /* Quick Driver can continue; ViolationPart stays hidden until list exists */ })
                .then(() => this.setState({ loadingViolationsList: false }));
        }
    }

    subscribeToValidation() {
        validationUtils.subscribeToValidationFailure(QUOTE_STEPS.QUICK_DRIVER,
            validationData => {
                this.setState({ updatingStore: false, continueClick: false, errors: validationData });
            });
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.meta.propsSource !== COMPONENTS.PROPS_SOURCES.APP_STORE) {
            return null;
        }

        const vehicles = nextProps.vehicles;
        const drivers = nextProps.drivers;
        // Re-hydrate if first merge was an empty shell before D2C/redux drivers arrived.
        const propsHaveDriver = !!(drivers && drivers[0] && drivers[0].firstName);
        const stateMissingDriver = !(prevState.drivers && prevState.drivers[0] && prevState.drivers[0].firstName);
        const shouldHydrate = !prevState.propsMerged || (propsHaveDriver && stateMissingDriver);

        if (!shouldHydrate || !(vehicles || drivers)) {
            return null;
        }

        const coverageConfig = nextProps.coverageConfig;
        const contactInfo = nextProps.contactInfo;
        const discounts = nextProps.discounts;
        const isNonOwners = nextProps.isNonOwners;
        const initialSlide = nextProps.initialSlide;
        const requireAgeGender = requireAgeGenderFor(nextProps.stateCode);
        const defaultLicenseState = getDefaultDlLicenseState(nextProps.stateCode);

        let canAddNewVehicle = nextProps.canAddNewVehicle;
        if (!canAddNewVehicle && vehicles && vehicles.length > 0 && vehicles.every(x => x != null)) {
            canAddNewVehicle = true;
        }

        const hydratedDrivers = (drivers || prevState.drivers || []).map((d: IDriver) => ({
            ...d,
            licenseState: d?.licenseState || defaultLicenseState,
            currentLicenseStatus: d?.currentLicenseStatus
                || QUOTE_QUESTIONS.YOUR_LICENSE.CURRENT_LICENSE_STATUS.ACTIVE,
        } as IDriver));

        const canAddNewDriver = hydratedDrivers.length > 0
            && hydratedDrivers.every((x, i) => isDriverReady(x, requireAgeGender, i));

        return {
            ...prevState,
            vehicles: vehicles || prevState.vehicles,
            canAddNewVehicle: canAddNewVehicle ?? prevState.canAddNewVehicle,
            drivers: hydratedDrivers,
            canAddNewDriver: !!canAddNewDriver,
            coverageConfig: coverageConfig ?? prevState.coverageConfig,
            contactInfo: contactInfo ?? prevState.contactInfo,
            discounts: discounts ?? prevState.discounts,
            isNonOwners: isNonOwners ?? prevState.isNonOwners,
            initialSlide: initialSlide,
            propsMerged: true
        };
    }

    componentWillUnmount() {
        validationUtils.unsubscribeFromValidationFailure(QUOTE_STEPS.QUICK_DRIVER);
    }

    onDriverAdd() {
        let _drivers = [...this.state.drivers];
        _drivers.push(createEmptyDriver(this.props.stateCode));

        this.setState({
            ...this.state, drivers: _drivers, activeDriverIndex: _drivers.length - 1, canAddNewDriver: false, errors: null
        });
    }

    onDriverDelete = (index: number) => {
        let _drivers = [...this.state.drivers];
        _drivers.splice(index, 1);

        const requireAgeGender = requireAgeGenderFor(this.props.stateCode);
        let _canAddNew = _drivers.length > 0 && _drivers.every((d, i) => isDriverReady(d, requireAgeGender, i));

        this.setState({
            ...this.state, drivers: _drivers, canAddNewDriver: _canAddNew, errors: null
        });
    }

    onDriverChange(driver: IDriver, index: number) {
        let _drivers = [...this.state.drivers];
        _drivers[index] = driver;

        const requireAgeGender = requireAgeGenderFor(this.props.stateCode);
        let allDriversValid = _drivers.every((d, i) => isDriverReady(d, requireAgeGender, i));

        this.setState({
            ...this.state,
            drivers: _drivers,
            activeDriverIndex: index,
            canAddNewDriver: allDriversValid
        });
    }

    onPhoneChange(value: string) {
        this.setState({
            ...this.state,
            contactInfo: {
                ...this.state.contactInfo,
                phone: value
            }
        });
    }

    next(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        event.preventDefault();

        this.setState({ updatingStore: true, errors: null }, () => {
            const defaultLicenseState = getDefaultDlLicenseState(this.props.stateCode);
            const updatedVehicles = [...this.state.vehicles];
            // Apply same defaults DriverPart2 shows so Continue does not need a UI poke first.
            const updatedDrivers = this.state.drivers.map(d => ({
                ...d,
                licenseState: d?.licenseState || defaultLicenseState,
                currentLicenseStatus: d?.currentLicenseStatus
                    || QUOTE_QUESTIONS.YOUR_LICENSE.CURRENT_LICENSE_STATUS.ACTIVE,
            } as IDriver));
            const coverageConfig = this.state.coverageConfig;
            const skipDiscounts = !hasQuickQuoteDiscounts(this.props.stateCode);
            const discounts = skipDiscounts
                ? { ...this.state.discounts, zeroOrOneViolationLast3Years: false }
                : this.state.discounts;
            const contactInfo = this.state.contactInfo;
            const isNonOwners = this.state.isNonOwners;

            this.props.updateAppState({
                quoteUid: this.props.quoteUid,
                vehicles: updatedVehicles,
                drivers: updatedDrivers,
                coverageConfig: coverageConfig,
                discounts: discounts,
                contactInfo: contactInfo,
                isNonOwners: isNonOwners,
                quickStep: skipDiscounts ? QUOTE_STEPS.QUICK_DISCOUNT : QUOTE_STEPS.QUICK_DRIVER,
                stateCode: this.props.stateCode
            } as IAppStateUpdate)
                .then(
                    () => this.props.history.push(skipDiscounts ? this.props.PATHS.RESULT : this.props.PATHS.QUICK_DISCOUNT),
                    () => {
                        this.setState({ ...this.state, updatingStore: false });
                    }
                );
        });
    }

    render() {
        const requireAgeGender = requireAgeGenderFor(this.props.stateCode);
        // Req #4: CA does not accept expired licenses — no point continuing to rating.
        const hasCaExpiredLicense = (this.props.stateCode || "").toUpperCase() === "CA"
            && this.state.drivers.some(d => d?.currentLicenseStatus === QUOTE_QUESTIONS.YOUR_LICENSE.CURRENT_LICENSE_STATUS.EXPIRED);

        return (
            <QuickQuoteStep id={this.props.id} stateCode={this.props.stateCode} >
                {
                    this.props.rateError
                        ?
                        <div>
                            <Alert variant={"warning"}>
                                <h5 className="text-center">
                                    <strong className="text-uppercase">Sorry we can't continue</strong>
                                    <br />
                                    {this.props.rateError}
                                </h5>
                            </Alert>
                        </div>
                        :
                        null
                }
                {
                    <div>
                        <Card className="quick-quote-data">
                            <Card.Body>
                                <Card className="drivers-card">
                                    <Card.Body>
                                        <Card.Title>
                                            {"DRIVERS"}
                                        </Card.Title>
                                        <ValidatableForm hasErrors={null != this.state.errors}>
                                            {
                                                this.state.drivers.map((driver, driverIndex) => (
                                                    <React.Fragment key={`driver-wrapper-${driverIndex}`}>
                                                        {driverIndex > 0 && (
                                                            <hr className="my-4" style={{ borderColor: '#dee2e6', borderWidth: '2px', borderStyle: 'dashed' }} />
                                                        )}

                                                        <DriverPart2
                                                            updatingStore={this.state.updatingStore}
                                                            driver={driver}
                                                            driverIndex={driverIndex}
                                                            isActive={this.state.activeDriverIndex === driverIndex}
                                                            onDriverChange={this.onDriverChange}
                                                            onDriverDelete={this.onDriverDelete}
                                                            errors={this.state.errors}
                                                            requireAgeGender={requireAgeGender}
                                                            defaultLicenseState={getDefaultDlLicenseState(this.props.stateCode)}
                                                            phoneValue={this.state.contactInfo?.phone}
                                                            onPhoneChange={this.onPhoneChange}
                                                            violationList={this.props.violationList}
                                                            stateCode={this.props.stateCode}
                                                        />
                                                    </React.Fragment>
                                                ))
                                            }
                                            <div className="btnContainer mt-3">
                                                <Button
                                                    className="btnAddDriver"
                                                    onClick={() => { this.onDriverAdd() }}
                                                    disabled={this.state.updatingStore || this.state.isNonOwners || !this.state.canAddNewDriver}>
                                                    <i className="fas fa-plus"></i> Add driver
                                                </Button>
                                            </div>
                                        </ValidatableForm>
                                    </Card.Body>
                                </Card>
                                <div className="break-2" />
                            </Card.Body>
                        </Card>

                        <div className="btnContainer">
                            <ButtonContinue
                                label={hasQuickQuoteDiscounts(this.props.stateCode) ? "Continue" : "Get Rate"}
                                disabled={this.state.updatingStore || hasCaExpiredLicense}
                                showLoading={this.state.updatingStore}
                                onClick={this.next} />
                        </div>
                    </div>
                }
            </QuickQuoteStep>
        );
    }
}

let mapProps = (state: IAppState, ownProps: IProps) => {
    // Do not mutate redux contactInfo — copy without email for this step display.
    const contactInfo = state.contactInfo
        ? { ...state.contactInfo, email: null }
        : state.contactInfo;

    return {
        quoteUid: state.quoteUid,
        stateCode: state.stateCode,
        vehicles: state.vehicles.length > 0 ? state.vehicles : [{} as IVehicle],
        coverageConfig: state.coverageConfig,
        drivers: state.drivers.length > 0 ? state.drivers : [createEmptyDriver(state.stateCode)],
        contactInfo: contactInfo,
        discounts: state.discountOption,
        isNonOwners: state.isNonOwners,
        rateError: state.rateError,
        violationList: state.violationList,
        meta: { propsSource: COMPONENTS.PROPS_SOURCES.APP_STORE }
    };
};

let mapDispatch = (dispatch: any, ownProps: IProps) => {
    var quoteActions = new QuoteActions(ownProps.quoteApiClient);

    return {
        reportCurrentStep: () => dispatch(quoteActions.quoteStepChanged(QUOTE_STEPS.QUICK_DRIVER)),
        updateAppState: (update: IAppStateUpdate) => dispatch(quoteActions.updateQuickData(update.quoteUid, update.vehicles, update.drivers, update.coverageConfig, update.discounts, update.contactInfo, update.isNonOwners, update.quickStep, update.stateCode)),
        updateViolationList: (update: IViolationList) => dispatch(quoteActions.updateViolatioList(update))
    };
}

export default connect(mapProps, mapDispatch)(QuickDriver);
